// src/pages/AggiungiUtente.jsx
import { useState, useEffect } from 'react';
import { authFetch } from '../utils/authFetch';
import { useNavigate } from 'react-router-dom';
import { Card, TableCell, TableHeadCell } from '../components/ui';

// Il backend è montato su /api, qui manteniamo lo stesso pattern che usi altrove
const BACKEND_URL = '/api';

const AggiungiUtente = () => {
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('tecnico');
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);

  const isCliente = role === 'cliente';

  const fetchUsers = async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/users`);
      if (!res.ok) {
        setError('Errore nel recupero utenti');
        return;
      }
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error('Errore nel recupero utenti:', err);
      setError('Errore nella richiesta');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ✅ quando selezioni "cliente" tolgo cognome e (se vuoi) immagine profilo
  useEffect(() => {
    if (isCliente) {
      setCognome('');
      setProfilePicture('');
    }
  }, [isCliente]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    // ✅ payload: se cliente, cognome può essere stringa vuota senza problemi
    const payload = {
      username,
      password,
      role,
      nome,
      cognome: isCliente ? '' : cognome,
      profilePicture: profilePicture.trim() !== '' ? profilePicture : undefined,
    };

    try {
      const res = await authFetch(`${BACKEND_URL}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        setMessage(data.message || 'Utente creato con successo');
        setUsername('');
        setPassword('');
        setRole('tecnico');
        setNome('');
        setCognome('');
        setProfilePicture('');
        fetchUsers(); // aggiorna la tabella
      } else {
        setError(data.error || 'Errore durante la creazione');
      }
    } catch (err) {
      console.error(err);
      setError('Errore nella richiesta');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-4xl space-y-6">
        {/* Titolo */}
        <Card className="shadow-sm px-4 py-3 md:px-6 md:py-4">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-800 text-center">
            Gestione Utenti
          </h2>
          <p className="mt-1 text-xs md:text-sm text-gray-500 text-center">
            Crea nuovi account per l&apos;azienda e visualizza gli utenti attivi.
          </p>
          {/* Bottone torna indietro */}
          <button
            onClick={() => navigate('/dashboard')}
            className="hidden md:inline-flex px-3 py-1.5 rounded-lg text-xs bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            ← Torna alla dashboard
          </button>
        </Card>

        {/* Form creazione utente */}
        <Card className="shadow-sm p-4 md:p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Aggiungi utente
          </h3>

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4"
          >
            <input
              type="text"
              placeholder="Nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              required
            />

            <input
              type="text"
              placeholder="Cognome"
              value={cognome}
              onChange={(e) => setCognome(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                isCliente ? 'bg-gray-50 text-gray-500' : ''
              }`}
              required={!isCliente}          // ✅ cliente: cognome NON required
              disabled={isCliente}           // ✅ cliente: lo disabilito proprio
            />

            <input
              type="email"
              placeholder="Email (username di accesso)"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              required
            />

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              required
            />

            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              required
            >
              <option value="admin">Admin</option>
              <option value="segreteria">Segreteria</option>
              <option value="gestione">Gestione</option>
              <option value="tecnico">Tecnico</option>

              {/* ✅ nuovo ruolo */}
              <option value="cliente">Cliente</option>
            </select>

            {/* opzionale: per cliente lo nascondo per evitare casino */}
            {!isCliente && (
              <input
                type="text"
                placeholder="URL immagine profilo (opzionale)"
                value={profilePicture}
                onChange={(e) => setProfilePicture(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 md:col-span-1"
              />
            )}

            <div className="md:col-span-2 mt-1">
              <button
                type="submit"
                className="w-full md:w-auto px-6 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition"
              >
                Crea utente
              </button>
            </div>
          </form>

          {(message || error) && (
            <div className="mt-4 space-y-1 text-sm">
              {message && (
                <p className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-lg">
                  {message}
                </p>
              )}
              {error && (
                <p className="text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
                  {error}
                </p>
              )}
            </div>
          )}
        </Card>

        {/* Tabella utenti */}
        <Card className="shadow-sm p-4 md:p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Utenti attivi
          </h3>

          {users.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nessun utente registrato al momento.
            </p>
          ) : (
            <>
              {/* Suggerimento mobile */}
              <p className="text-[11px] text-gray-400 mb-2 md:hidden">
                Scorri orizzontalmente la tabella per visualizzare tutti i dati.
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-xs md:text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700">
                      <TableHeadCell className="px-3 py-2 text-left">
                        Nome
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Cognome
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Email
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Ruolo
                      </TableHeadCell>
                      <TableHeadCell className="px-3 py-2 text-left">
                        Matricola
                      </TableHeadCell>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr
                        key={user._id}
                        className="border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <TableCell className="px-3 py-2">
                          {user.nome || '-'}
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          {user.cognome || '-'}
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          <span className="block truncate max-w-[150px] md:max-w-xs">
                            {user.username}
                          </span>
                        </TableCell>
                        <TableCell className="px-3 py-2 capitalize">
                          {user.role}
                        </TableCell>
                        <TableCell className="px-3 py-2">
                          {user.matricola || '-'}
                        </TableCell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>

        <button
          onClick={() => navigate('/dashboard')}
          className="mt-2 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
        >
          ← Torna alla dashboard
        </button>
      </div>
    </div>
  );
};

export default AggiungiUtente;
